import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const INK = "#1E293B";
const INK_SOFT = "#64748B";
const LINE = "#E2E8F0";
const PRIMARY = "#2563EB";
const PRIMARY_SOFT = "#BFDBFE";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';

export const LICENSE_FILE_ACCEPT = "image/jpeg,image/jpg,image/png,.pdf,application/pdf";

type LicenseUploadCardProps = {
  title: string;
  fileName?: string | null;
  fileUrl?: string | null;
  error?: string;
  accept?: string;
  formatsHint?: string;
  onFileSelect: (file: File) => void;
};

export const LicenseUploadCard: React.FunctionComponent<LicenseUploadCardProps> = ({
  title,
  fileName,
  fileUrl,
  error,
  accept = LICENSE_FILE_ACCEPT,
  formatsHint = "Supported formats: PDF, JPG, PNG",
  onFileSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const pickFile = (file?: File | null) => {
    if (!file) return;
    onFileSelect(file);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    pickFile(file);
    event.target.value = "";
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    pickFile(event.dataTransfer.files?.[0]);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 300,
        minHeight: 260,
        bgcolor: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: "10px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        fontFamily: BODY_FONT,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 700,
            fontSize: 13,
            color: INK,
            letterSpacing: "-0.01em",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<FileUploadOutlinedIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => inputRef.current?.click()}
          sx={{
            flexShrink: 0,
            bgcolor: PRIMARY,
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            fontFamily: BODY_FONT,
            fontSize: 12,
            borderRadius: "6px",
            px: 1.25,
            py: 0.25,
            minHeight: 28,
            boxShadow: "none",
            "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
          }}
        >
          Attach files
        </Button>
      </Box>

      <Box
        component="label"
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragging(false);
        }}
        onDrop={onDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 0.5,
          minHeight: 120,
          px: 1.5,
          py: 2,
          borderRadius: "8px",
          flex: 1,
          border: `1.5px dashed ${dragging ? PRIMARY : PRIMARY_SOFT}`,
          bgcolor: dragging ? "#EFF6FF" : "#F8FBFF",
          cursor: "pointer",
          transition: "border-color .15s ease, background-color .15s ease",
          "&:hover": {
            borderColor: PRIMARY,
            bgcolor: "#EFF6FF",
          },
        }}
      >
        <input
          ref={inputRef}
          hidden
          accept={accept}
          type="file"
          onChange={onInputChange}
        />
        <CloudUploadRoundedIcon sx={{ fontSize: 28, color: PRIMARY }} />
        <Box sx={{ fontSize: 13, fontWeight: 600, color: PRIMARY, lineHeight: 1.3 }}>
          Click to upload or drag and drop
        </Box>
        <Box sx={{ fontSize: 11, color: INK_SOFT, lineHeight: 1.3 }}>{formatsHint}</Box>
      </Box>

      {fileName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
            px: 0.75,
            py: 0.5,
            borderRadius: "6px",
            bgcolor: "#F8FAFC",
            border: `1px solid ${LINE}`,
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: PRIMARY, flexShrink: 0 }} />
          {fileUrl ? (
            <Link
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {fileName}
            </Link>
          ) : (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {fileName}
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Box sx={{ fontSize: 12, color: "error.main", px: 0.25 }}>{error}</Box>
      )}
    </Box>
  );
};

export default LicenseUploadCard;
